import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { useGetDicListQuery } from '@/api/common';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditPersonalInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			sex: null,
			...toAgeFormValues(),
		};

		const { data: maritalStatusDicList } = useGetDicListQuery(
			'marital_status',
			{ refetchOnMountOrArgChange: true }
		);
		const { data: placeBirthDicList } = useGetDicListQuery('place_of_birth', {
			refetchOnMountOrArgChange: true,
		});
		const { data: dialectDicList } = useGetDicListQuery('dialect', {
			refetchOnMountOrArgChange: true,
		});

		useEffect(() => {
			if (details) methods.reset({ ...details, ...toAgeFormValues(details) });
		}, [details]);

		const formSchema = Yup.object().shape({
			nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
			nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
			...ageSchemaFields(t, {
				getOriginalEstimatedAge: () => details?.estimatedAge,
			}),
			sex: Yup.string().required(t('errorMessage.pleaseSelect')),
			placeOfBirth: Yup.string().required(t('errorMessage.pleaseSelect')),
			placeOfBirthSpecify: Yup.string().when('placeOfBirth', {
				is: (placeOfBirth: string) => placeOfBirth && placeOfBirthIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			yearOfArrivingHk: Yup.string().when('placeOfBirth', {
				is: (placeOfBirth: string) => placeOfBirth && !placeOfBirthIsHK(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchPlaceOfBirth = methods.watch('placeOfBirth');

		const placeOfBirthIsOther = () => {
			const obj = placeBirthDicList?.data.find(
				(item: any) => item.id == watchPlaceOfBirth
			);
			if (obj && obj.labelEn === 'Others') return true;
			return false;
		};
		const placeOfBirthIsHK = () => {
			const obj = placeBirthDicList?.data.find(
				(item: any) => item.id == watchPlaceOfBirth
			);
			if (obj && obj.labelEn === 'Hong Kong') return true;
			return false;
		};

		useImperativeHandle(ref, () => ({
			getFormData: () => {
				const values = methods.getValues();
				return { ...values, ...toAgePayload(values) };
			},
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.personalInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.name_zh') + '*'}
								/>
							</Grid>
							<AppAgeInput control={methods.control} />
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkid'
									label={t('preAdmission.HKID')}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='dialect'
									label={t('preAdmission.dialect')}
									options={(dialectDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='maritalStatus'
									label={t('preAdmission.maritalStatus')}
									options={(maritalStatusDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppDropDownMenu
									control={methods.control}
									name='placeOfBirth'
									label={t('preAdmission.placeBirth') + '*'}
									options={(placeBirthDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
									handleChange={() => {
										methods.clearErrors('placeOfBirthSpecify');
										methods.clearErrors('yearOfArrivingHk');
										methods.setValue('placeOfBirthSpecify', '');
										methods.setValue('yearOfArrivingHk', '');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!placeOfBirthIsOther()}
									control={methods.control}
									name='placeOfBirthSpecify'
									label={`${t('preAdmission.placeBirthSpecify')}${
										!placeOfBirthIsOther() ? '' : '*'
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={placeOfBirthIsHK()}
									control={methods.control}
									name='yearOfArrivingHk'
									label={`${t('preAdmission.yearArrivingHK')}${
										placeOfBirthIsHK() ? '' : '*'
									}`}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditPersonalInformationForm.displayName = 'EditPersonalInformationForm';

export default EditPersonalInformationForm;
